import { useSwitchTenantMutation } from '@dieterApi/user/useUserSwitchTenantMutation';
import { User } from '@dieterApi/user/useUserQuery';
import { Loading } from '@components/ui';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';

/**
 * Deeplinks from Klaus carry the company they are meant for (`?company=<id>`):
 * the session switches to it before the page renders, then the parameter
 * leaves the URL. A company the user is not a member of shows a hint instead
 * of the page, so nothing opens silently in the wrong company.
 * Returns what to render instead of the page, or null when the page may render.
 * Plan: klaus-ki/docs/dieter-aufgaben-plan.md.
 */
export function useCompanyDeeplink(user: User | undefined): ReactNode | null {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [switchTenant] = useSwitchTenantMutation();
  const [failed, setFailed] = useState(false);

  const companyId = new URLSearchParams(location.search).get('company');
  // Only after login and MFA: switchTenant needs the full session.
  const ready = !!user?.mfaVerified;
  const isActive = !companyId || user?.company?.id === companyId;

  useEffect(() => {
    if (!companyId || !ready) return;
    const dropParam = () => {
      const params = new URLSearchParams(location.search);
      params.delete('company');
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash });
    };
    if (isActive) {
      dropParam();
      return;
    }
    setFailed(false);
    switchTenant({ variables: { companyId } })
      .then(dropParam)
      .catch(() => setFailed(true));
  }, [companyId, isActive, ready]);

  if (!ready) return null;
  if (failed && companyId) {
    return (
      <div className="2xl:container mx-auto mt-24 flex flex-col items-center gap-6 text-center">
        <h1>{t('common.deeplink.company_not_accessible_title', 'Kein Zugriff auf diese Firma')}</h1>
        <p className="max-w-[480px]">
          {t(
            'common.deeplink.company_not_accessible',
            'Der Link gehört zu einer Firma, in der dein Konto nicht Mitglied ist. Melde dich mit dem Konto an, das zu dieser Firma gehört.'
          )}
        </p>
        <Link to="/dashboard">{t('common.deeplink.to_dashboard', 'Zu deinem Dashboard')}</Link>
      </div>
    );
  }
  if (!isActive) {
    return (
      <div className="flex items-center justify-center mt-24">
        <Loading />
      </div>
    );
  }
  return null;
}
